import { useCallback, useState } from 'react';

/**
 * Fade an image in at the frame it actually decodes.
 *
 * Spread the result onto an `<img>`. Until the bitmap is ready the element is
 * transparent (`.media-fade`), and `is-loaded` reveals it — so media arrives
 * instead of snapping into place whenever the network happens to deliver it.
 *
 * Three details make this reliable rather than mostly-working:
 *
 * - The `ref` checks `complete`. A cached image is already decoded before React
 *   attaches `onLoad`, and that event has been and gone — without this check
 *   every cached avatar would sit at opacity 0 forever.
 * - `onError` reveals too. A broken image must show its alt text / broken glyph,
 *   not stay invisible.
 * - The class is composed rather than replaced, so a caller can keep its own.
 *
 * @param extraClass classes to keep alongside the fade classes
 */
export const useImageFade = (extraClass = '') => {
  const [loaded, setLoaded] = useState(false);
  const reveal = useCallback(() => setLoaded(true), []);

  const ref = useCallback((node) => {
    if (node?.complete) setLoaded(true);
  }, []);

  return {
    ref,
    onLoad: reveal,
    onError: reveal,
    className: `${extraClass} media-fade${loaded ? ' is-loaded' : ''}`.trim(),
  };
};

export default useImageFade;
